import { createContext, useContext, useState, useEffect } from 'react'

const SweepstakesContext = createContext({})

// Cookie utilities for first-time visitor detection
const COOKIE_NAME = 'lp_sweepstakes_shown'
const COOKIE_DURATION_DAYS = 365 // Show once per year

const setCookie = (name, value, days) => {
  const expires = new Date()
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000)
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`
}

const getCookie = (name) => {
  const nameEQ = name + '='
  const ca = document.cookie.split(';')
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i]
    while (c.charAt(0) === ' ') c = c.substring(1, c.length)
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length)
  }
  return null
}

export const useSweepstakes = () => {
  const context = useContext(SweepstakesContext)
  if (!context) {
    throw new Error('useSweepstakes must be used within SweepstakesProvider')
  }
  return context
}

export const SweepstakesProvider = ({ children }) => {
  // Form data state
  const [formData, setFormData] = useState({
    priorityChoice: '',
    fullName: '',
    email: '',
    phone: '',
    weddingDate: '',
    weddingLocation: '',
    termsAccepted: false,
    smsTransactionalConsent: false,
    smsMarketingConsent: false
  })

  // UI state
  const [currentStep, setCurrentStep] = useState(1)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showThankYou, setShowThankYou] = useState(false)
  const [errors, setErrors] = useState({})

  const TOTAL_STEPS = 5

  // Check if first-time visitor
  const isFirstTimeVisitor = () => {
    return getCookie(COOKIE_NAME) === null
  }

  // Mark as shown
  const markSweepstakesShown = () => {
    setCookie(COOKIE_NAME, 'true', COOKIE_DURATION_DAYS)
  }

  // Update form data
  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    // Clear error for this field when user updates it
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }))
    }
  }

  // Validation functions for each step
  const validateStep = (step) => {
    const newErrors = {}

    switch (step) {
      case 1: // Priority choice
        if (!formData.priorityChoice) {
          newErrors.priorityChoice = 'Please select an option'
        }
        break
      case 2: // Full name
        if (!formData.fullName.trim()) {
          newErrors.fullName = 'Please enter your full name'
        } else if (formData.fullName.trim().split(' ').length < 2) {
          newErrors.fullName = 'Please enter your full name (first and last)'
        }
        break
      case 3: // Contact info
        if (!formData.email.trim()) {
          newErrors.email = 'Please enter your email'
        } else if (!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(formData.email)) {
          newErrors.email = 'Please enter a valid email address'
        }
        if (formData.phone && !/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(formData.phone.replace(/\s/g, ''))) {
          newErrors.phone = 'Please enter a valid phone number'
        }
        if (!formData.smsTransactionalConsent) {
          newErrors.smsTransactionalConsent = 'Please agree to receive notifications to continue'
        }
        break
      case 4: // Wedding details
        if (!formData.weddingDate.trim()) {
          newErrors.weddingDate = 'Please enter a date or select TBA'
        }
        if (!formData.weddingLocation.trim()) {
          newErrors.weddingLocation = 'Please enter your wedding location'
        }
        break
      case 5: // Terms
        if (!formData.termsAccepted) {
          newErrors.termsAccepted = 'Please accept the terms and conditions'
        }
        break
      default:
        break
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Navigation
  const goToNextStep = () => {
    if (validateStep(currentStep)) {
      if (currentStep < TOTAL_STEPS) {
        setCurrentStep(prev => prev + 1)
      }
      return true
    }
    return false
  }

  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1)
    }
  }

  // Modal controls
  const openModal = () => {
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    // Mark as shown so it doesn't reappear when user dismisses it
    markSweepstakesShown()
    // Reset form when modal is closed
    if (!showThankYou) {
      resetForm()
    }
  }

  // Reset form
  const resetForm = () => {
    setFormData({
      priorityChoice: '',
      fullName: '',
      email: '',
      phone: '',
      weddingDate: '',
      weddingLocation: '',
      termsAccepted: false,
      smsTransactionalConsent: false,
      smsMarketingConsent: false
    })
    setCurrentStep(1)
    setErrors({})
    setShowThankYou(false)
  }

  // Submit form
  const submitForm = async () => {
    if (!validateStep(5)) {
      return false
    }

    setIsSubmitting(true)

    try {
      const response = await fetch('/api/sweepstakes/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit entry')
      }

      // Success!
      setShowThankYou(true)
      markSweepstakesShown()

      // Close modal after 3 seconds
      setTimeout(() => {
        closeModal()
        resetForm()
      }, 3000)

      return true
    } catch (error) {
      console.error('Sweepstakes submission error:', error)
      setErrors({ submit: error.message })
      return false
    } finally {
      setIsSubmitting(false)
    }
  }

  const value = {
    // Form data
    formData,
    updateFormData,

    // Navigation
    currentStep,
    totalSteps: TOTAL_STEPS,
    goToNextStep,
    goToPreviousStep,

    // Modal state
    isModalOpen,
    openModal,
    closeModal,

    // Submission
    isSubmitting,
    submitForm,
    showThankYou,

    // Validation
    errors,
    validateStep,

    // Utilities
    isFirstTimeVisitor,
    markSweepstakesShown,
    resetForm
  }

  return (
    <SweepstakesContext.Provider value={value}>
      {children}
    </SweepstakesContext.Provider>
  )
}
